import React, { useRef, useState } from "react";
import { Modal, Button, Input } from "antd";
import SignatureCanvas from "react-signature-canvas";

const { TextArea } = Input;

/**
 * ReusabaleSignatureModal — A reusable signature capture/view modal.
 *
 * Props:
 * @param {boolean}  open              – Controls modal visibility
 * @param {string}   title             – Modal header title
 * @param {string}   description       – Warning/info text shown above the canvas (optional)
 * @param {boolean}  loading           – Shows loading state on the Done button
 * @param {boolean}  showNotes         – Whether to render the notes textarea (default: false)
 * @param {string}   notesPlaceholder  – Placeholder for notes textarea (default: "Enter notes")
 * @param {Function} onDone            – Called with (signatureDataURL, note?) when Done is clicked
 * @param {Function} onCancel          – Called when the modal is dismissed
 * @param {string}   signatureUrl      – If provided, shows the image instead of the canvas (view mode)
 */
const ReusabaleSignatureModal = ({
    open,
    title = "Signature",
    description,
    loading = false,
    showNotes = false,
    notesPlaceholder = "Enter notes",
    onDone,
    onCancel,
    signatureUrl,
    signatureDate,
}) => {
    const sigCanvas = useRef(null);
    const inputRef = useRef(null);
    const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);

    const handleClear = () => {
        sigCanvas.current.clear();
        setIsSignatureEmpty(true);
    };

    const handleDone = () => {
        if (!onDone) return;
        const signatureData = sigCanvas.current.toDataURL("image/png");
        const note = showNotes
            ? inputRef.current?.resizableTextArea?.textArea?.value?.trim() ?? ""
            : "";
        onDone(signatureData, note);
    };

    const handleCancel = () => {
        if (!signatureUrl) handleClear();
        onCancel?.();
    };

    return (
        <Modal
            title={title}
            open={open}
            onCancel={handleCancel}
            closable
            footer={null}
            maskClosable={false}
            destroyOnClose
        >
            {description && (
                <p style={{ color: "red", marginTop: 5, marginBottom: 8 }}>
                    {description}
                </p>
            )}

            {signatureUrl ? (
                <>
                    <div
                        style={{
                            border: "1px dotted #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 460,
                            height: 250,
                            background: "#fff",
                        }}
                    >
                        <img
                            src={signatureUrl}
                            alt="Signature"
                            style={{ width: 460, height: 250, objectFit: "contain" }}
                        />
                    </div>

                    <div style={{ marginTop: 12, display: "flex", justifyContent: signatureDate ? "space-between" : "flex-end", alignItems: "center" }}>
                        {signatureDate && (
                            <span style={{ color: "gray", fontSize: 12 }}>
                                Signed on: {new Date(signatureDate).toLocaleDateString()}
                            </span>
                        )}
                        <Button className="ant-btn-modal" onClick={handleCancel}>
                            Close
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    {showNotes && (
                        <TextArea
                            ref={inputRef}
                            placeholder={notesPlaceholder}
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            style={{ marginBottom: 10 }}
                        />
                    )}

                    <div style={{ border: "1px dotted #ccc" }}>
                        <SignatureCanvas
                            canvasProps={{ width: 460, height: 250 }}
                            ref={sigCanvas}
                            onBegin={() => setIsSignatureEmpty(false)}
                        />
                    </div>

                    <div style={{ marginTop: 12, textAlign: "right" }}>
                        <Button
                            className="ant-btn-modal"
                            type="primary"
                            danger
                            style={{ marginRight: 4 }}
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                        <Button
                            className="ant-btn-modal"
                            type="primary"
                            loading={loading}
                            disabled={isSignatureEmpty}
                            onClick={handleDone}
                        >
                            Done
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default ReusabaleSignatureModal;